import React from "react";

import { type MutableProseNodeAttrs, ProseReact } from "..";
import { faBolt, faCog, faPlus, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import cx from "classnames";
import { isEqual, sortBy } from "lodash";
import { type AttributeSpec, Fragment, Node as ProseNode } from "prosemirror-model";
import { type Command, EditorState, Plugin, PluginKey, TextSelection, Transaction } from "prosemirror-state";
import { Decoration, DecorationSet, EditorView } from "prosemirror-view";
import { el } from "redom";
import uuid from "uuid";

import { showFeatureModal, UpgradeIcon } from "~/billing";
import UserMultiSelector, {
	type MultiValueType,
	type UserMultiSelectorOption,
} from "~/common/UserSelect/UserMultiSelector";
import { type UserSelectorValue } from "~/common/UserSelect/UserSelector";
import { cloneNode, type DOMComponent, render } from "~/lib/dom";
import { useID } from "~/lib/hooks";
import { confirm } from "~/lib/prompts";
import { setBlockType } from "~/lib/prose/commands";
import { Extension } from "~/lib/prose/extension";
import { getContext, getCurrentUser } from "~/lib/prose/plugins/context";
import { createPosBookmark, PosBookmark } from "~/lib/prose/plugins/posbookmark";
import {
	blockDescendants,
	closestBlock,
	closestBlockDepth,
	findBlockAndPos,
	isNodeHasPrefixDecor,
	nextBlockInsertionPos,
} from "~/lib/prose/utils";
import { decodeGlobalId } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { listFormat, setTooltip } from "~/lib/utils";
import type { AssignableDropdownItemType } from "~/notes/AssignableDropdown";
import type { EditorContextParticipant } from "~/stream/NoteCard";
import { Button } from "~/ui";
import ArrowDropdown, { DropdownItem } from "~/ui/ArrowDropdown";
import Checkbox from "~/ui/legacy/Checkbox";
import { HelpCenterIcon, Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";

import {
	BUILT_IN_SNIPPETS,
	convertBuiltInToCustom,
	CUSTOM_TEMPLATE_ID,
	type CustomSectionConfigs,
	normalizeFragmentJSON,
	renderSnippet,
	type Snippet,
	SnippetEditor,
	SnippetPreview,
	SnippetSelect,
} from "../snippet";
import { AI_TOPICS_TEMPLATE_ID } from "./aiTopics";
import { getBlockBoundaryState } from "./blockBoundary";
import { isCollapsed } from "./collapse";
import { makePromptPlaceholderPlugin } from "./promptPlaceholder";

import buttonStyles from "~/ui/button/Button.module.css";

import styles from "./prompt.module.css";

export const AI_HEADERS_DECO_ORDER = 0;
export const AI_TOPICS_DECO_ORDER = -1;
export const BLOCK_ANNOTATION_ORDER = -2;
const PROMPT_RESPONSE_DECO_ORDER = -3;

interface PromptAttrs {
	promptAllRequired?: boolean;
	promptExceptionUsers?: readonly string[];
	promptResponseTemplate?: Snippet;
}

interface PromptResponseAttrs {
	promptResponseTo: string | null;
}

export const promptHeadingsEnabled = !!window.INITIAL_STATE?.promptHeadingsEnabled;

export const promptAttrs: Readonly<{ [k: string]: AttributeSpec }> = {
	/**
	 * `true` if all note users must respond to this prompt.
	 */
	promptAllRequired: { default: false },
	/**
	 * A list of user Relay IDs. If all note users are required to respond, this
	 * is a list of users who do not need to respond. If not all note users are
	 * required to respond, this is a list of those who do need to respond.
	 */
	promptExceptionUsers: { default: [] },
	/**
	 * The response template. We need to store the name and ID because they will be used in the selection menu.
	 * It can be null because we have a default response template in the code: see `insertSnippetAfterBlockTr`
	 */
	promptResponseTemplate: { default: null },
};

export const promptResponseAttrs: Readonly<{ [k: string]: AttributeSpec }> = {
	promptResponseTo: { default: null },
};

function defaultResponseTemplate() {
	return BUILT_IN_SNIPPETS[0];
}

export function getPromptMenuItems(
	view: EditorView,
	node: ProseNode,
	getPos: () => number,
	inTemplate = false,
	customSectionConfigs?: CustomSectionConfigs,
): AssignableDropdownItemType[] {
	if (!promptHeadingsEnabled) return [];
	const promptConfigMenuItem: AssignableDropdownItemType = {
		id: "prompt-config",
		action: close => {
			close();
			return openConfigurationModal(
				view,
				node,
				createPosBookmark(view.state, getPos()),
				undefined,
				customSectionConfigs,
			);
		},
		icon: faCog,
		right: "Configure Section",
		disabled: !view.editable,
	};
	const promptRemoveMenuItem: AssignableDropdownItemType = {
		id: "prompt-remove",
		action: close => {
			close();
			if (!inTemplate) {
				trackSectionEdited(view.state, node.attrs.blockId, "removed", "kebab_menu");
			}
			view.dispatch(
				setPromptAttrs(view.state.tr, getPos(), {
					promptResponseTemplate: undefined,
				}),
			);
			return true;
		},
		icon: faTimes,
		right: "Remove Section",
		disabled: !view.editable,
	};
	const menuItems = [promptConfigMenuItem];
	if (promptEnabled(node)) {
		menuItems.push(promptRemoveMenuItem);
	}
	return menuItems;
}

function headerSizeBeforeSubheader(node: ProseNode): number {
	let size = 0;
	for (let i = 0; i < node.childCount; i += 1) {
		const child = node.child(i);
		if (child.type.name === "hard_break") break;
		size += child.nodeSize;
	}
	return size;
}

export const DUMMY_USER: EditorContextParticipant = {
	__typename: "User",
	type: "MEMBER",
	email: "Placeholder mention",
	isGuest: false,
	isActive: true,
	isConfigured: true,
	image: "",
	fullName: "attendee",
	firstName: "attendee",
	id: "dummy",
	isTopUser: false,
	isExternal: false,
	internalAttributes: {
		invitable: false,
	},
};

function ConfigurationModal({
	view,
	initialNode,
	nodePosBookmark,
	close,
	prefillSectionName,
	customSectionConfigs,
}: {
	view: EditorView;
	initialNode: ProseNode;
	nodePosBookmark: PosBookmark;
	close: () => void;
	/**
	 * When this prop is true and the block has no content, the value of the
	 * snippet's defaultSectionName will be added to the block's content.
	 */
	prefillSectionName?: boolean;
	customSectionConfigs?: CustomSectionConfigs;
}) {
	const formId = useID();
	const requireUsersId = useID();
	const excludeUsersId = useID();

	const hasAccessToPromptHeadings = !!getContext(view.state)?.hasAccessToPromptHeadings;
	const [allRequired, setAllRequired] = React.useState(() => promptAllRequired(initialNode));
	const [exceptionUsers, setExceptionUsers] = React.useState(() => [...promptExceptionUsers(initialNode)]);
	const [snippet, setSnippet] = React.useState<Snippet>(promptResponseTemplate(initialNode) || BUILT_IN_SNIPPETS[0]);
	// use callback ref pattern instead of useRef
	const [editorRef, setEditorRef] = React.useState<ProseReact | undefined>();
	// the initial editorView.doc for custom template, used to check if the template has been edited
	const initialCustomTemplateDoc = React.useRef<ProseNode | undefined>();
	const [customSectionChanged, setCustomSectionChanged] = React.useState(false);
	const [customSectionEmpty, setCustomSectionEmpty] = React.useState(false);

	const editorCallbackRef = React.useCallback(
		node => {
			if (node !== null) {
				// save ref for later use
				setEditorRef(node);
				// save the initial doc for later use
				if (snippet?.id === CUSTOM_TEMPLATE_ID) {
					initialCustomTemplateDoc.current = node.editorView?.state.doc;
				}
			}
		},
		[snippet?.id],
	);

	const setAllRequiredTrue = React.useCallback(() => {
		if (!hasAccessToPromptHeadings) return;
		setAllRequired(true);
	}, [setAllRequired, hasAccessToPromptHeadings]);
	const setAllRequiredFalse = React.useCallback(() => {
		if (!hasAccessToPromptHeadings) return;
		setAllRequired(false);
	}, [setAllRequired, hasAccessToPromptHeadings]);

	function onFormSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();

		const { tr } = view.state;

		const replacedSnippet =
			snippet?.id === CUSTOM_TEMPLATE_ID
				? {
						...snippet,
						fragmentJSON: editorRef?.editorView?.state.doc.content.toJSON(),
						customTemplateId: uuid.v4(),
					}
				: snippet;

		const promptAttrs: PromptAttrs = {
			promptAllRequired: allRequired,
			promptExceptionUsers: exceptionUsers,
			promptResponseTemplate: replacedSnippet,
		};

		const currentNodeAndPos = findBlockAndPos(tr.doc, initialNode.attrs.blockId);
		let posInHeading: number, headingBlock: ProseNode;
		if (
			currentNodeAndPos &&
			currentNodeAndPos[0].attrs.blockId === initialNode.attrs.blockId &&
			currentNodeAndPos[0].type === initialNode.type
		) {
			const [node, pos] = currentNodeAndPos;
			// The heading is still in the document and is still the same type, so
			// we can just apply the changes to it.
			posInHeading = pos + 1 + headerSizeBeforeSubheader(node);
			headingBlock = node;
			setPromptAttrs(tr, pos, promptAttrs);
		} else {
			// The node the modal opened for has either been changed to a different
			// type or has been deleted. We'll create a new node after the position
			// the old node was at.
			headingBlock = initialNode.type.create(
				{ ...initialNode.attrs, ...promptAttrs, blockId: uuid.v4() },
				initialNode.content,
			);

			const mappedPos = nodePosBookmark.mapToNow(),
				insertPos = nextBlockInsertionPos(tr.doc.resolve(mappedPos));

			tr.insert(insertPos, headingBlock);
			posInHeading = insertPos + 1 + headerSizeBeforeSubheader(headingBlock);
		}

		const blockIsEmpty = headingBlock.content.size === 0 || headingBlock.textContent.trim().length === 0;
		if (blockIsEmpty && prefillSectionName && snippet) {
			tr.insertText(snippet.defaultSectionName);
			posInHeading += snippet.defaultSectionName.length;
		}

		if (!promptEnabled(initialNode) && promptEnabled(headingBlock)) {
			// This modal can only be opened from the kebab menu, so this means the
			// user clicked "Configure Section" in the kebab menu and then added a
			// template, thus enabling the prompt on this block.
			trackSectionEdited(view.state, headingBlock.attrs.blockId, "added", "kebab_menu");
		}

		// Set selection after possibly inserting content and adjusting posInHeading
		tr.setSelection(new TextSelection(tr.doc.resolve(posInHeading)));
		const posInHeadingBookmark = createPosBookmark(view.state, posInHeading);

		view.dispatch(tr.setMeta("addToHistory", false));
		close();

		// If the user clicks into the modal (other than just clicking save), their
		// browser selection will move into the modal. Later, when we focus the
		// EditorView, ProseMirror will convert that into a selection in the
		// document, which ends up being from:1,to:1. Here we re-set the selection
		// and focus the view a little bit later to avoid the selection moving to
		// the top of the document.
		setTimeout(() => {
			const { tr } = view.state;
			view.dispatch(tr.setSelection(new TextSelection(tr.doc.resolve(posInHeadingBookmark.mapToNow()))));
			view.focus();
		}, 0);
	}

	const onEditorContentChanged = React.useCallback(
		(editorState: EditorState) => {
			const { doc } = editorState;
			// once it's changed we don't need to check the content for the next time
			// otherwise we check if doc at the moment equals to the initial doc we saved before
			const isDocChanged =
				customSectionChanged ||
				(!!doc && !!initialCustomTemplateDoc.current && !doc.eq(initialCustomTemplateDoc.current));
			setCustomSectionChanged(isDocChanged);
			// the editor is empty if visually there's no content inside the editor (e.g. an unstyled node with no text content)
			// for doc that has empty node with prefix decoration, the textContent is empty, so we need to check their type
			const isEmpty =
				doc?.content.size === 0 ||
				(doc.textContent === "" && !!doc.firstChild && !isNodeHasPrefixDecor(doc.firstChild));
			setCustomSectionEmpty(isEmpty);
		},
		[customSectionChanged],
	);

	const closeWithoutSave = React.useCallback(() => {
		// prompt unsaved message if a custom template has been edited
		if (snippet?.id === CUSTOM_TEMPLATE_ID && customSectionChanged) {
			return confirm("Changes you've made so far will not be saved.", {
				yesText: "Discard",
				noText: "Keep editing",
				simple: true,
				title: "Discard unsaved changes?",
			});
		}
		return true;
	}, [snippet, customSectionChanged]);

	const onUserSelectChange = React.useCallback(
		(newUsers: MultiValueType<UserMultiSelectorOption>) => {
			if (!hasAccessToPromptHeadings) return;
			setExceptionUsers(newUsers.map(user => user.value));
		},
		[hasAccessToPromptHeadings],
	);

	const onClose = React.useCallback(async () => {
		if (await closeWithoutSave()) {
			close();
		}
	}, [close, closeWithoutSave]);

	const [showExceptionUsers, setShowExceptionUsers] = React.useState(!!exceptionUsers.length);
	const toggleShowExceptionUsers = React.useCallback(() => {
		setShowExceptionUsers(v => !v);
	}, [setShowExceptionUsers]);
	const actuallyRequiringResponses = allRequired || !!exceptionUsers.length;
	const [forceShowingRequiredUsers, setForceShowingRequiredUsers] = React.useState(actuallyRequiringResponses);

	const setRequiredToEmpty = React.useCallback(() => {
		setAllRequired(false);
		setExceptionUsers([]);
	}, [setAllRequired, setExceptionUsers]);

	const onRequireResponsesChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (!hasAccessToPromptHeadings) {
				showFeatureModal("prompt_headings");
				return;
			}
			if (!e.target.checked) {
				setRequiredToEmpty();
				setForceShowingRequiredUsers(false);
				setShowExceptionUsers(false);
			} else {
				setForceShowingRequiredUsers(true);
				setAllRequiredTrue();
			}
		},
		[
			setRequiredToEmpty,
			setForceShowingRequiredUsers,
			setAllRequiredTrue,
			setShowExceptionUsers,
			hasAccessToPromptHeadings,
		],
	);

	React.useEffect(() => {
		if (!showExceptionUsers) {
			setExceptionUsers([]);
		}
	}, [showExceptionUsers, setExceptionUsers]);

	const changeToCustomTemplate = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			if (snippet) {
				const newSnippet = convertBuiltInToCustom(snippet);
				setSnippet(newSnippet);
			}
		},
		[snippet],
	);

	const participants: EditorContextParticipant[] = React.useMemo(() => {
		return getContext(view.state)?.participants || [];
	}, [view.state]);

	// Extract participant IDs for idIn prop to restrict search to only participants
	const participantIds = React.useMemo(() => {
		return participants.map(p => p.id);
	}, [participants]);

	// Convert exceptionUsers (string[]) to UserMultiSelectorOption[] for selectedUsers
	const selectedUsers = React.useMemo((): UserMultiSelectorOption[] => {
		const result: UserMultiSelectorOption[] = [];
		for (const id of exceptionUsers) {
			const participant = participants.find(p => p.id === id);
			if (participant) {
				result.push({
					value: participant.id,
					label: participant.fullName,
					avatar: {
						id: participant.id,
						fullName: participant.fullName,
						isGuest: "isGuest" in participant ? participant.isGuest : false,
						image: participant.image || "",
						email: "email" in participant ? participant.email || "" : "",
					} satisfies UserSelectorValue,
				});
			}
		}
		return result;
	}, [exceptionUsers, participants]);

	const requiredUsersSectionEnabled =
		hasAccessToPromptHeadings && (forceShowingRequiredUsers || actuallyRequiringResponses);

	// disable the save button if the custom template has no content
	const customContentEmpty = React.useMemo(() => {
		return snippet?.id === CUSTOM_TEMPLATE_ID && customSectionEmpty;
	}, [snippet?.id, customSectionEmpty]);

	return (
		<Modal
			onClose={onClose}
			className={styles.configModal}
			lockIgnoredAttrs={["data-reach-menu-button", "data-reach-menu-items"]}
		>
			<Modal.Header>
				<Modal.Title>Section Configuration</Modal.Title>
				<Modal.HeaderActions onClose={onClose}>
					<HelpCenterIcon articleId="5992965" title="Learn more about Sections" />
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body dark className={styles.configModalBody}>
				<form id={formId} onSubmit={onFormSubmit}>
					<label>
						<p className={styles.snippetSelectorLabel}>Template</p>
						<SnippetSelect
							value={snippet}
							onChange={setSnippet}
							initialValue={promptResponseTemplate(initialNode) || BUILT_IN_SNIPPETS[0]}
							className={styles.snippetSelector}
						/>
					</label>
					{snippet.id === CUSTOM_TEMPLATE_ID ? (
						<div className={styles.editor}>
							<SnippetEditor
								editorRef={editorCallbackRef}
								snippet={snippet}
								onChange={onEditorContentChanged}
								view={view}
								currentUser={DUMMY_USER}
								customSectionConfigs={customSectionConfigs}
							/>
						</div>
					) : (
						<div className={styles.previewArea}>
							{snippet.id !== AI_TOPICS_TEMPLATE_ID && (
								<div className={styles.customizeButton__container}>
									<span className={styles.customizeButton} onClick={changeToCustomTemplate}>
										customize template
									</span>
								</div>
							)}
							<SnippetPreview snippet={snippet} currentUser={DUMMY_USER} />
						</div>
					)}
					<div className={cx(styles.checkboxLabel, styles.requireResponseCheckboxLabel)}>
						<Checkbox
							id={requireUsersId}
							green
							checked={requiredUsersSectionEnabled}
							onChange={onRequireResponsesChange}
							className={styles.checkbox}
							disabled={!snippet}
						/>
						<label htmlFor={requireUsersId}>
							<div className={styles.twoLines}>
								<p>Require meeting participants to add to this section</p>
								<small>Reminder notifications will be sent in advance of the meeting.</small>
							</div>
						</label>
						{!hasAccessToPromptHeadings && <UpgradeIcon />}
					</div>
					{requiredUsersSectionEnabled ? (
						<>
							<div className={styles.requiredUsersDropdownContainer}>
								<span>Required users:</span>
								<ArrowDropdown
									forceCloseOnOutsideClick
									className={styles.allRequiredSelect}
									heading="Required users"
									text={allRequired ? "All meeting attendees" : "Specific users"}
									disabled={!snippet}
								>
									<DropdownItem onClick={setAllRequiredTrue}>All meeting attendees</DropdownItem>
									<DropdownItem onClick={setAllRequiredFalse}>Specific users</DropdownItem>
								</ArrowDropdown>
							</div>
							{allRequired && (
								<div className={cx(styles.checkboxLabel, styles.includeExcludeUsersCheckboxLabel)}>
									<Checkbox
										green
										id={excludeUsersId}
										checked={showExceptionUsers}
										onChange={toggleShowExceptionUsers}
										label="Exclude users"
										className={cx(styles.checkbox, styles.includeExcludeUsersCheckbox)}
										disabled={!snippet}
									/>
									<label htmlFor={excludeUsersId}>
										<p>Exclude user(s)</p>
									</label>
								</div>
							)}
							{showExceptionUsers || !allRequired ? (
								<>
									{!allRequired && <p className={styles.userSelectLabel}>Select attendees:</p>}
									<UserMultiSelector
										ranked
										className={styles.userSelect}
										selectedUsers={selectedUsers}
										onChange={onUserSelectChange}
										isDisabled={!snippet}
										idIn={participantIds}
										defaultOptions
									/>
								</>
							) : null}
						</>
					) : null}
				</form>
			</Modal.Body>
			<Modal.Footer>
				<Button type="button" onClick={onClose} size="normal">
					Cancel
				</Button>
				<Button form={formId} disabled={customContentEmpty} type="submit" variant="primary" size="normal">
					Save
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export function openConfigurationModal(
	view: EditorView,
	node: ProseNode,
	posBookmark: PosBookmark,
	prefillSectionName?: boolean,
	customSectionConfigs?: CustomSectionConfigs,
): boolean {
	__globalModalContext?.open(({ close }) => (
		<ConfigurationModal
			close={close}
			view={view}
			initialNode={node}
			nodePosBookmark={posBookmark}
			prefillSectionName={prefillSectionName}
			customSectionConfigs={customSectionConfigs}
		/>
	));
	return true;
}

/** Check if the given node has an enabled prompt */
export function promptEnabled(node: ProseNode): boolean {
	return promptEnabledAttrs(node.attrs);
}

export function promptEnabledAttrs(attrs: PromptAttrs): boolean {
	return !!attrs.promptResponseTemplate;
}

/**
 * Check if the given node's prompt requires all attendees to respond.
 *
 * Note that this does not check whether the node has an enabled prompt.
 */
export function promptAllRequired(node: ProseNode): boolean {
	return !!node.attrs.promptAllRequired;
}

/**
 * Get the exception users for the given node's prompt.
 *
 * Note that this does not check whether the node has an enabled prompt.
 */
export function promptExceptionUsers(node: ProseNode): readonly string[] {
	return Array.isArray(node.attrs.promptExceptionUsers)
		? node.attrs.promptExceptionUsers.filter(u => typeof u === "string")
		: [];
}

/**
 * Get the blockId of the prompt node that the given node is a response to.
 */
export function promptResponseTo(node: ProseNode): string | null {
	return node.attrs.promptResponseTo ?? null;
}

export function promptResponseTemplate(node: ProseNode): Snippet | undefined {
	return node.attrs.promptResponseTemplate ?? undefined;
}

/*
 * Get the list of users that are required to respond to the prompt in `node`
 * Returns empty array when it's not enabled for `node`.
 */
export function promptRequiredUsers(node: ProseNode, state: EditorState): EditorContextParticipant[] {
	if (!promptHeadingsEnabled || !getContext(state)?.hasAccessToPromptHeadings) return [];
	const exceptionUserIds = new Set(promptExceptionUsers(node));
	const noteUsers = (getContext(state)?.participants || []) as EditorContextParticipant[];
	if (promptAllRequired(node)) {
		return noteUsers.filter(u => !exceptionUserIds.has(u.id));
	}
	const res = noteUsers.filter(u => exceptionUserIds.has(u.id));
	return res;
}

/**
 * Get the list of users that haven't responded to a required prompt in `node`
 */
export function promptNoRespondUsers(node: ProseNode, state: EditorState): EditorContextParticipant[] {
	const responsesState = promptResponsesPluginKey.getState(state);
	// Return empty instead all all required because most likely it's in a broken state
	if (!responsesState) return [];
	const blockRespondedUserIds = responsesState[node.attrs.blockId]?.responded || new Set();
	return promptRequiredUsers(node, state).filter(u => !blockRespondedUserIds.has(u.id));
}

/**
 * Modify the prompt attributes of the node at position `pos`.
 *
 * If the node at that position does not support prompts, using this function
 * will result in an error.
 */
export function setPromptAttrs<T extends PromptAttrs | PromptResponseAttrs>(
	tr: Transaction,
	pos: number,
	attrs: T,
): Transaction {
	const node = tr.doc.nodeAt(pos);
	if (!node) {
		Sentry.captureMessage(`No node at pos ${pos}`);
		return tr;
	}

	return tr.setNodeMarkup(pos, undefined, {
		...node.attrs,
		...attrs,
	});
}

export function serializePromptAttrstoDOMAttrs(node: ProseNode): { [k: string]: string } {
	return {
		"data-prompt-all-required": promptAllRequired(node).toString(),
		"data-prompt-exception-users": JSON.stringify(promptExceptionUsers(node)),
		"data-prompt-response-template": JSON.stringify(promptResponseTemplate(node)),
	};
}

export function deserializePromptAttrsFromDOMAttrs(dom: HTMLElement): PromptAttrs {
	return {
		promptAllRequired: dom.dataset.promptAllRequired === "true",
		promptExceptionUsers: JSON.parse(dom.dataset.promptExceptionUsers || "[]"),
		promptResponseTemplate: JSON.parse(dom.dataset.promptResponseTemplate || "null"),
	};
}

function sectionAnalyticsProperties(state: EditorState): { [k: string]: unknown } {
	const context = getContext(state);
	if (!context) {
		return {};
	}
	return {
		/* eslint-disable camelcase */
		note_id: !context.inTemplate ? decodeGlobalId(context.noteId)[1] : undefined,
		stream_id: !context.inTemplate ? decodeGlobalId(context.streamId)[1] : undefined,
		stream_type: context.streamType,
		in_template: !!context.inTemplate,
		/* eslint-enable camelcase */
	};
}

export function trackSectionEdited(
	state: EditorState,
	blockId: string,
	action: "added" | "removed",
	source: string,
): void {
	if (getContext(state)?.inTemplatesAdmin) return;
	dispatchAnalyticsTrack("Note Section Edited", {
		...sectionAnalyticsProperties(state),
		// eslint-disable-next-line camelcase
		block_key: blockId,
		action,
		source,
	});
}

export function trackSectionResponse(state: EditorState, blockId: string, source: string): void {
	dispatchAnalyticsTrack("Note Section Response Added", {
		...sectionAnalyticsProperties(state),
		// eslint-disable-next-line camelcase
		response_to_block_key: blockId,
		source,
	});
}

export function changeToPromptHeading(configureImmediately = true, defaultAttrs: PromptAttrs = {}): Command {
	return (state, dispatch, view) => {
		// Convert the selected block into a header-one node with the prompt
		// enabled. This part happens synchronously.
		if (
			!setBlockType(state.schema.nodes["header-one"], {
				promptResponseTemplate: defaultResponseTemplate(),
				...defaultAttrs,
			})(state, dispatch)
		) {
			return false;
		}

		if (configureImmediately) {
			if (!view) return false;

			// By this point, `state` is probably out of date. Need to use view.state
			// going forward. We shadow it here so that it's not possible to refer to
			// the stale EditorState beyond this point.
			const { state } = view;

			const { $from, $to } = state.selection;
			const fromBlock = closestBlock($from),
				toBlock = closestBlock($to);

			// Selection must be within a single block
			if (fromBlock !== toBlock) return false;

			const nodePosBookmark = createPosBookmark(state, $from.pos);
			openConfigurationModal(view, fromBlock, nodePosBookmark, true);
		}
		return true;
	};
}

const promptResponseToPluginKey = new PluginKey<void>("prompt-response-to");

const promptResponseToPlugin = new Plugin({
	key: promptResponseToPluginKey,
	appendTransaction(trs, _oldState, newState) {
		// Every client should update promptResponseTo for its own changes, no need
		// to handle remote changes here.
		if (trs.every(tr => !tr.docChanged || tr.getMeta("remoteChange"))) return undefined;

		let currentSection: ProseNode | undefined;
		let newTr = newState.tr;

		let madeChanges = false;
		blockDescendants(newState.doc, (node, start) => {
			if (node.type.name === "header-one") {
				// Top-level headings can't be responses to other headings
				if (node.attrs.level === "1" && promptResponseTo(node)) {
					setPromptAttrs(newTr, start, { promptResponseTo: null });
					madeChanges = true;
				}
				if (promptEnabled(node)) {
					// Regardless of the level of this header, any items below it belong
					// to it since it's a prompt.
					currentSection = node;
				} else if (currentSection && Number(node.attrs.level) <= Number(currentSection.attrs.level)) {
					// This heading is either the same level or higher than the current
					// prompt header, so it ends that section.
					currentSection = undefined;
				}
				return;
			}
			// Either sets the attr to the current section or to undefined if the
			// current section isn't a prompt.
			const currentSectionId: string | null = currentSection?.attrs.blockId ?? null;
			if (currentSectionId !== promptResponseTo(node)) {
				setPromptAttrs(newTr, start, { promptResponseTo: currentSectionId });
				madeChanges = true;
			}
		});

		if (madeChanges) return newTr;
		return undefined;
	},
});

type PromptResponsesState = Readonly<{
	[blockId: string]: Readonly<{ responded: Readonly<Set<string>>; missing: Readonly<Set<string>> }>;
}>;
type MutablePromptResponsesState = {
	[blockId: string]: { responded: Set<string>; missing: Set<string> };
};
export const promptResponsesPluginKey = new PluginKey<PromptResponsesState>("prompt-responses");

function calculateResponsesState(state: EditorState): PromptResponsesState {
	const { doc } = state;
	const newResponsesState: MutablePromptResponsesState = {};

	function ensureResponseState(key: string): MutablePromptResponsesState[string] {
		return (newResponsesState[key] ??= {
			missing: new Set(),
			responded: new Set(),
		});
	}

	blockDescendants(doc, node => {
		if (promptEnabled(node) && node.attrs.blockId) {
			// There can be some in-between state where the same blockId is used by multiple blocks
			const { missing, responded } = ensureResponseState(node.attrs.blockId);
			promptRequiredUsers(node, state).forEach(user => {
				if (!responded.has(user.id)) {
					missing.add(user.id);
				}
			});
		}
		const responseTo = promptResponseTo(node);
		if (responseTo !== null) {
			ensureResponseState(responseTo).responded.add(node.attrs.creator);
			ensureResponseState(responseTo).missing.delete(node.attrs.creator);
		}
	});

	return newResponsesState;
}

const promptResponsesPlugin = new Plugin({
	key: promptResponsesPluginKey,
	state: {
		init(_config, state) {
			return calculateResponsesState(state);
		},
		apply(tr, value, _oldState, newState) {
			if (!tr.docChanged) return value;
			return calculateResponsesState(newState);
		},
	},
});

export function userName(user: EditorContextParticipant): string {
	return "firstName" in user && user.firstName ? user.firstName : user.fullName;
}

export class PromptExtension extends Extension {
	name = "prompt";

	get plugins() {
		if (promptHeadingsEnabled) {
			if (this.options.inTemplate) {
				return [makePromptPlaceholderPlugin(this, { inTemplate: true }), NoResponseBar.makeTemplatePlugin(this)];
			}
			return [
				promptResponseToPlugin,
				promptResponsesPlugin,
				makePromptPlaceholderPlugin(this, { inTemplate: false }),
				NoResponseBar.makePlugin(this),
			];
		}
		return [];
	}
}

type HeaderButtonData = {
	node: ProseNode;
	view: EditorView;
	getPos: boolean | (() => number);
};

const plusButton = render(
	<Button size="wide" variant="secondary" className={styles.plusButton}>
		<FontAwesomeIcon icon={faPlus} />
	</Button>,
) as HTMLButtonElement;

if (!(plusButton instanceof HTMLButtonElement)) {
	throw new Error("plusButton is not HTMLButtonElement");
}

export class PlusButton implements DOMComponent<HeaderButtonData> {
	el: HTMLButtonElement;
	data?: HeaderButtonData;
	constructor(data: HeaderButtonData) {
		this.el = cloneNode(plusButton, true);
		this.update(data);
		this.el.onclick = this.onClick;
	}
	update = (data: HeaderButtonData) => {
		const editable = data.view.editable && !(getContext(data.view.state)?.locked ?? false);
		this.el.classList.toggle(buttonStyles.disabled, !editable);
		this.el.disabled = !editable;
		this.data = data;
	};
	onClick = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (!this.data) return;
		const { view, getPos, node } = this.data;
		if (!view.editable) return;
		if (typeof getPos === "boolean") return;
		const tr = view.state.tr;
		const snippet = promptResponseTemplate(node);
		if (!snippet) return;
		insertSnippetAfterBlockTr(view.state, tr, getPos() + 1, snippet);
		trackSectionResponse(view.state, node.attrs.blockId, "plus_button");
		view.dispatch(tr);
		view.focus();
	};
}

const boltButton = render(
	<Button
		size="wide"
		variant="secondary"
		className={styles.boltButton}
		{...setTooltip("Get attendees to fill out", "left center", true, true, "180px")}
	>
		<FontAwesomeIcon icon={faBolt} />
	</Button>,
) as HTMLButtonElement;

if (!(boltButton instanceof HTMLButtonElement)) {
	throw new Error("boltButton is not HTMLButtonElement");
}

export class BoltButton implements DOMComponent<HeaderButtonData> {
	el: HTMLButtonElement;
	data?: HeaderButtonData;
	constructor(data: HeaderButtonData) {
		this.el = cloneNode(boltButton, true);
		this.update(data);
		this.el.onclick = this.onClick;
	}
	update = (data: HeaderButtonData) => {
		const editable = data.view.editable && !(getContext(data.view.state)?.locked ?? false);
		this.el.classList.toggle(buttonStyles.disabled, !editable);
		this.el.disabled = !editable;
		this.data = data;
	};
	onClick = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (!this.data) return;
		const { view, getPos } = this.data;
		if (!view.editable) return;
		if (typeof getPos === "boolean") return;
		const tr = view.state.tr;
		view.dispatch(
			setPromptAttrs(tr, getPos(), {
				promptAllRequired: true,
			}),
		);
	};
}

export function insertSnippetAfterBlockTr(state: EditorState, tr: Transaction, pos: number, snippet: Snippet) {
	const { doc } = tr;
	const $pos = doc.resolve(pos);
	const blockDepth = closestBlockDepth($pos);
	const posAfterBlock = $pos.end(blockDepth) + 1;
	const currentUser = getCurrentUser(state);
	if (!currentUser) throw new Error("Can't get current user");
	const content =
		snippet.id === CUSTOM_TEMPLATE_ID
			? Fragment.fromJSON(state.schema, normalizeFragmentJSON(snippet.fragmentJSON, { currentUser }))
			: Fragment.from(renderSnippet(snippet.blocksJSON, state.schema, { currentUser }));
	const block = $pos.node(blockDepth);
	if (promptEnabled(block)) {
		blockDescendants(content, n => {
			if ("promptResponseTo" in n.attrs) {
				(n.attrs as MutableProseNodeAttrs).promptResponseTo = block.attrs.blockId;
			}
		});
	}
	tr.insert(posAfterBlock, content);
	tr.setSelection(TextSelection.create(tr.doc, posAfterBlock + content.size - 1));
	return tr;
}

type NoResponseBarData = (
	| {
			inTemplate: false;
	  }
	| {
			inTemplate: true;
			node: ProseNode;
	  }
) & {
	missingUserIds: Readonly<Set<string>>;
	state: EditorState;
	editable: boolean;
};

export class NoResponseBar implements DOMComponent<NoResponseBarData> {
	el: HTMLElement;
	data?: NoResponseBarData;
	requiredUsers?: EditorContextParticipant[];
	constructor(data: NoResponseBarData) {
		this.el = el("div", { class: styles.noResponseBar, contentEditable: false });
		this.update(data);
	}
	renderUserList = (data: NoResponseBarData) => {
		const noteUsers = (getContext(data.state)?.participants || []) as EditorContextParticipant[];
		const currentUser = getCurrentUser(data.state);
		const requiredUsers = sortBy(
			noteUsers.filter(u => data.missingUserIds.has(u.id)),
			u => (u.id === currentUser?.id ? -1 : 0),
		);
		this.el.innerHTML = "";
		const userNameEls = requiredUsers.map(u => el("b", u.id === currentUser?.id ? "You" : userName(u))).filter(Boolean);
		if (userNameEls.length > 4) {
			const diff = userNameEls.length - 3;
			const userNamesInOther = listFormat(requiredUsers.map(u => userName(u)).slice(3)).join("");
			const moreUsers = el("b", { title: userNamesInOther }, `${diff} others`);
			userNameEls.splice(3, diff, moreUsers);
		}
		if (userNameEls.length) {
			const isOrAre = requiredUsers.length === 1 && requiredUsers[0].id !== currentUser?.id ? "is" : "are";
			this.el.append(
				el("div", { class: styles.noResponseBarNames }, listFormat(userNameEls)),
				" ",
				isOrAre,
				" required to add to this section",
			);
		}
		this.requiredUsers = requiredUsers;
	};
	renderTemplateNotice = (data: Extract<NoResponseBarData, { node: ProseNode }>) => {
		this.el.innerHTML = "";
		const text = (() => {
			const allRequired = promptAllRequired(data.node);
			const exceptions = promptExceptionUsers(data.node);
			if (allRequired) {
				if (exceptions.length) {
					return "Specific users";
				}
				return "All required attendees";
			} else {
				if (exceptions.length) {
					return "Specific users";
				}
				return "";
			}
		})();
		if (text) {
			this.el.append(
				el("div", { class: styles.noResponseBarNames }, text),
				" will be required to fill out this section in advance of the meeting.",
			);
		}
	};
	update = (data: NoResponseBarData) => {
		const { missingUserIds } = data;
		if (!data.inTemplate) {
			if (data.editable) {
				this.el.style.display = "";
			} else {
				this.el.style.display = "none";
			}
		}
		if (isEqual(missingUserIds, this.data?.missingUserIds)) {
			this.data = data;
			return;
		}
		if (!data.inTemplate) {
			this.renderUserList(data);
		} else {
			this.renderTemplateNotice(data);
		}
		this.data = data;
	};
	static makeTemplatePlugin = (props: { editorView?: EditorView }) => {
		const createDecorationSetInTemplate = (state: EditorState) => {
			// Template should be usuall small.. So optimize it later
			const newDecorations: Decoration[] = [];
			blockDescendants(state.doc, (block, start) => {
				if (promptEnabled(block)) {
					const component = new NoResponseBar({
						state,
						missingUserIds: new Set(),
						inTemplate: true,
						node: block,
						editable: props.editorView?.editable ?? false,
					});
					newDecorations.push(Decoration.widget(start + block.nodeSize, component.el));
				}
			});
			return DecorationSet.create(state.doc, newDecorations);
		};
		return new Plugin({
			props: {
				decorations: state => {
					return createDecorationSetInTemplate(state);
				},
			},
		});
	};
	static makePlugin = (props: { editorView?: EditorView }) => {
		const BLOCKID_TO_COMPONENT: { [key: string]: NoResponseBar } = {};
		const getDecorationDOMForBlock = (state: EditorState, blockId: string, missingUserIds: Readonly<Set<string>>) => {
			let component = BLOCKID_TO_COMPONENT[blockId];
			if (!component) {
				component = new NoResponseBar({
					state,
					missingUserIds,
					inTemplate: false,
					editable: props.editorView?.editable ?? false,
				});
				BLOCKID_TO_COMPONENT[blockId] = component;
			} else {
				component.update({ state, missingUserIds, inTemplate: false, editable: props.editorView?.editable ?? false });
			}
			return component.el;
		};
		const createDecorationSet = (state: EditorState) => {
			if (!getContext(state)?.hasAccessToPromptHeadings) return DecorationSet.empty;

			const newDecorations = new Set<Decoration>();

			const responsesState = promptResponsesPluginKey.getState(state);
			const blockBoundaryState = getBlockBoundaryState(state);
			if (!responsesState || !blockBoundaryState) {
				return;
			}
			for (const [blockId, value] of Object.entries(responsesState)) {
				const blockBoundary = blockBoundaryState.blockBoundaries[blockId];
				if (!blockBoundary) continue;
				const [_start, end, block] = blockBoundary;
				if (isCollapsed(state, block)) continue;
				if (value.missing.size) {
					const deco = Decoration.widget(end, getDecorationDOMForBlock(state, block.attrs.blockId, value.missing), {
						blockId: block.attrs.blockId,
						side: PROMPT_RESPONSE_DECO_ORDER,
					});
					newDecorations.add(deco);
				}
			}
			return DecorationSet.create(state.doc, Array.from(newDecorations));
		};
		return new Plugin({
			props: {
				decorations: state => {
					return createDecorationSet(state);
				},
			},
		});
	};
}
